import { useAuth } from '@saay/auth-web';
import { formatCount, statusLabel } from '@saay/formatting';
import { DirectionProvider } from '@saay/shadcn/components/direction';
import { Empty, EmptyContent, EmptyHeader, EmptyTitle } from '@saay/shadcn/components/empty';
import { Skeleton } from '@saay/shadcn/components/skeleton';
import { buttonVariants } from '@saay/shadcn/components/button';
import { AuthCallbackRoute, AuthGate } from '@saay/web-app';
import {
  BookOpenCheckIcon,
  BuildingIcon,
  CalendarDaysIcon,
  CoinsIcon,
  CreditCardIcon,
  FileTextIcon,
  FlameIcon,
  HomeIcon,
  InboxIcon,
  LandmarkIcon,
  type LucideIcon,
  MegaphoneIcon,
  ReceiptTextIcon,
  ShieldCheckIcon,
  WalletIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
  StoreIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  type RouteObject,
  useLocation,
} from 'react-router';

import { AdminShell, type ShellLink } from './components/admin-shell.js';
import { FinancialAdjustmentSheet } from './components/financial-adjustment.js';
import { GlobalHeaderSearch } from './components/global-header-search.js';
import { usePageTitle } from './components/guard.js';
import { IdleTimeoutGuard } from './components/idle-timeout.js';
import { type AdminRoute, approvalsNavLabel, moreRoutes, phoneBarRoutes, routeFor } from './nav.js';
import { ApprovalDetailSheet, ApprovalsPage } from './pages/approvals.js';
import { useMerchantRequestCount } from './pages/merchant-requests.js';
import { AuditPage } from './pages/audit.js';
import { BuyerDetailSheet } from './pages/buyer-detail.js';
import { PhysicalCardExportRedirect, PhysicalCardsPage } from './pages/physical-cards.js';
import { BuyerProvisionSheet } from './pages/buyer-provision.js';
import { BuyersPage } from './pages/buyers-list.js';
import { CategoriesSheet } from './pages/categories.js';
import { DashboardPage } from './pages/dashboard.js';
import { MerchantCreateSheet } from './pages/merchant-create.js';
import { MerchantDetailSheet } from './pages/merchant-detail.js';
import { MerchantsPage } from './pages/merchants-list.js';
import { MePage } from './pages/me.js';
import { MyCommissionsPage } from './pages/my-commissions.js';
import {
  MarketingAssignmentPage,
  MarketingAssignmentsPage,
} from './pages/marketing-assignments.js';
import {
  MarketingLayout,
  MarketingRecipientPage,
  MarketingRecipientsPage,
  MarketingStructurePage,
  MarketingStructuresPage,
  MarketingSummaryPage,
} from './pages/marketing.js';
import {
  CommissionHoldsPage,
  CommissionPayoutPage,
  CommissionPayoutsPage,
  MarketingCommissionsPage,
} from './pages/marketing-payouts.js';
import { SessionsPage } from './pages/sessions.js';
import { CashSettlementBatchSheet } from './pages/cash-settlements.js';
import { PoliciesPage } from './pages/policies.js';
import { DelinquencyCasePage, DelinquencyCasesPage } from './pages/delinquency.js';
import {
  PaymentDetailSheet,
  PaymentsPage,
  ReturnRequestDetailSheet,
  ReturnRequestsPage,
} from './pages/payments.js';
import { WalletsPage } from './pages/wallets.js';
import { WriteOffsPage } from './pages/write-offs.js';
import { EnrollmentCreateSheet, EnrollmentRequestSheet } from './pages/enrollment.js';
import { OrganizationCreateSheet, OrganizationDetailSheet } from './pages/organization-detail.js';
import { OrganizationsPage } from './pages/organizations.js';
import { CreditProgramDetailSheet, TargetingRunSheet } from './pages/credit-program-detail.js';
import { CreditProgramCreateSheet, CreditProgramsPage } from './pages/credit-programs.js';
import { TargetingSheet } from './pages/targeting.js';
import { canSeeArea } from './permissions.js';
import { useAdminDashboard, useAdminSelf } from './queries.js';
import { PlatformAccessPage } from './pages/platform-access.js';
import {
  CalendarPage,
  SettlementAttemptsPage,
  SettlementBatchSheet,
  SettlementCashPage,
  SettlementHoldsPage,
  SettlementRecoveryPage,
  SettlementReconciliationPage,
  SettlementsPage,
} from './pages/settlements.js';

/** Screens opened from another screen highlight that screen's navigation item. */
const OPENED_FROM: Readonly<Record<string, string>> = {
  '/delinquency-cases': '/payments',
  '/exports': '/merchants',
  '/merchant-categories': '/merchants',
};

function isCurrent(route: AdminRoute, pathname: string): boolean {
  const owner = Object.entries(OPENED_FROM).find(
    ([path]) => pathname === path || pathname.startsWith(`${path}/`),
  )?.[1];
  const path = owner ?? pathname;
  return path === route.path || path.startsWith(`${route.path}/`);
}

/** The navigation order, group, and icon of each destination (the D-93 admin sample). */
const NAV_PLACES: readonly {
  readonly group?: string;
  readonly icon: LucideIcon;
  readonly path: string;
}[] = [
  { icon: HomeIcon, path: '/dashboard' },
  { icon: InboxIcon, path: '/approvals' },
  { icon: CoinsIcon, path: '/my-commissions' },
  { group: 'مشتریان', icon: StoreIcon, path: '/merchants' },
  { group: 'مشتریان', icon: BuildingIcon, path: '/organizations' },
  { group: 'مشتریان', icon: UsersIcon, path: '/buyers' },
  { group: 'مشتریان', icon: CreditCardIcon, path: '/physical-cards' },
  { group: 'مشتریان', icon: SparklesIcon, path: '/credit-programs' },
  { group: 'مالی', icon: FileTextIcon, path: '/sales' },
  { group: 'مالی', icon: ReceiptTextIcon, path: '/payments' },
  { group: 'مالی', icon: WalletIcon, path: '/wallets' },
  { group: 'مالی', icon: LandmarkIcon, path: '/settlements' },
  { group: 'مالی', icon: FlameIcon, path: '/write-offs' },
  { group: 'مالی', icon: BookOpenCheckIcon, path: '/audit' },
  { group: 'تنظیمات', icon: SlidersHorizontalIcon, path: '/policies' },
  { group: 'تنظیمات', icon: ShieldCheckIcon, path: '/platform-access' },
  { group: 'تنظیمات', icon: CalendarDaysIcon, path: '/calendar' },
  { group: 'تنظیمات', icon: MegaphoneIcon, path: '/marketing' },
  { icon: UserIcon, path: '/me' },
];

const NAV_GROUPS = [undefined, 'مشتریان', 'مالی', 'تنظیمات'] as const;

function navPlace(path: string) {
  return NAV_PLACES.find((place) => place.path === path);
}

export function Layout() {
  const self = useAdminSelf();
  const { controller, principal } = useAuth();
  const location = useLocation();
  const showApprovalsBadge = self.data !== undefined && canSeeArea(self.data, 'approvals');
  const dashboard = useAdminDashboard(showApprovalsBadge);
  const merchantRequests = useMerchantRequestCount(showApprovalsBadge ? self.data : undefined);
  const waitingCount = (dashboard.data?.approvals.decidableCount ?? 0) + merchantRequests.count;
  const badgeMore = merchantRequests.more;
  const waiting = waitingCount > 0 || badgeMore;
  const toItem = (route: AdminRoute): ShellLink => {
    const approvals = route.path === '/approvals' && waiting;
    return {
      current: isCurrent(route, location.pathname),
      href: route.path,
      icon: navPlace(route.path)?.icon ?? FileTextIcon,
      label: route.labelFa,
      ...(route.shortLabelFa === undefined ? {} : { shortLabel: route.shortLabelFa }),
      ...(approvals
        ? {
            ariaLabel: approvalsNavLabel(waitingCount, badgeMore),
            badge: `${formatCount(waitingCount)}${badgeMore ? '+' : ''}`,
          }
        : {}),
    };
  };
  const account = routeFor('/me');
  const destinations = moreRoutes(self.data)
    .filter((route) => route.path !== '/me')
    .sort(
      (left, right) =>
        NAV_PLACES.findIndex((place) => place.path === left.path) -
        NAV_PLACES.findIndex((place) => place.path === right.path),
    );

  const name = self.data?.user.displayNameFa ?? principal?.displayName ?? 'کاربر';
  const roleLabel = (self.data?.roles ?? [])
    .map((role) => statusLabel('fixedRole', role))
    .join('، ');

  return (
    <>
      <AdminShell
        {...(account === undefined ? {} : { account: toItem(account) })}
        groups={NAV_GROUPS.map((group) => ({
          items: destinations.filter((route) => navPlace(route.path)?.group === group).map(toItem),
          ...(group === undefined ? {} : { label: group }),
        })).filter((group) => group.items.length > 0)}
        headerSearch={<GlobalHeaderSearch self={self.data} />}
        onLogout={() => void controller.logout()}
        phoneItems={phoneBarRoutes(self.data).map(toItem)}
        user={{ name, roleLabel }}
      >
        <Outlet />
      </AdminShell>
      <IdleTimeoutGuard />
    </>
  );
}

function NotFoundPage() {
  usePageTitle('صفحه پیدا نشد');
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>
          <h1>صفحه پیدا نشد</h1>
        </EmptyTitle>
      </EmptyHeader>
      <EmptyContent>
        <Link className={buttonVariants({ variant: 'outline' })} to="/">
          رفتن به صفحهٔ اصلی
        </Link>
      </EmptyContent>
    </Empty>
  );
}

/** `/`: the prototype's default page is the dashboard; a role without it lands on «حساب من». */
function IndexRedirect() {
  const self = useAdminSelf();
  if (self.status === 'pending') {
    return (
      <div aria-busy="true" className="flex flex-col gap-2">
        <span className="sr-only">در حال بارگذاری…</span>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-2/3" />
      </div>
    );
  }
  const home =
    self.data !== undefined && canSeeArea(self.data, 'dashboard')
      ? '/dashboard'
      : self.data !== undefined && canSeeArea(self.data, 'myCommissions')
        ? '/my-commissions'
        : '/me';
  return <Navigate replace to={home} />;
}

export function MeRoute() {
  usePageTitle('حساب من');
  return <MePage />;
}

/** `/sessions` «دستگاه‌های فعال» (AC §27): the signed-in admin's sessions, inside the app. */
export function SessionsRoute() {
  usePageTitle('دستگاه‌های فعال');
  return <SessionsPage />;
}

export const routes: RouteObject[] = [
  { element: <AuthCallbackRoute />, path: '/auth/callback' },
  {
    children: [
      {
        children: [
          { element: <IndexRedirect />, index: true },
          { element: <MeRoute />, path: 'me' },
          { element: <SessionsRoute />, path: 'sessions' },
          { element: <DashboardPage />, path: 'dashboard' },
          {
            children: [
              { element: null, index: true },
              { element: <ApprovalDetailSheet />, path: ':approvalRequestId' },
            ],
            element: <ApprovalsPage />,
            path: 'approvals',
          },
          { element: <AuditPage />, path: 'audit' },
          {
            element: (
              <>
                <AuditPage />
                <FinancialAdjustmentSheet />
              </>
            ),
            path: 'audit/financial-adjustments/new',
          },
          // Route-driven sheets (prototype `.panel-sheet`): the list stays underneath, so a deep link
          // opens the sheet over the list and closing it returns to the list.
          {
            children: [
              { element: null, index: true },
              { element: <MerchantCreateSheet />, path: 'new' },
              { element: <MerchantDetailSheet />, path: ':merchantId' },
            ],
            element: <MerchantsPage />,
            path: 'merchants',
          },
          {
            children: [{ element: <CategoriesSheet />, index: true }],
            element: <MerchantsPage />,
            path: 'merchant-categories',
          },
          {
            children: [
              { element: null, index: true },
              { element: <BuyerProvisionSheet />, path: 'new' },
              { element: <BuyerDetailSheet />, path: ':buyerId' },
            ],
            element: <BuyersPage />,
            path: 'buyers',
          },
          {
            children: [
              { element: null, index: true },
              { element: <OrganizationCreateSheet />, path: 'new' },
              { element: <OrganizationDetailSheet />, path: ':organizationId' },
              { element: <EnrollmentCreateSheet />, path: ':organizationId/enrollments/new' },
              {
                element: <EnrollmentRequestSheet />,
                path: ':organizationId/enrollments/:enrollmentRequestId',
              },
            ],
            element: <OrganizationsPage />,
            path: 'organizations',
          },
          {
            children: [
              { element: null, index: true },
              { element: <CreditProgramCreateSheet />, path: 'new' },
              { element: <TargetingSheet />, path: 'targeting' },
              { element: <CreditProgramDetailSheet />, path: ':programId' },
              { element: <TargetingSheet />, path: ':programId/targeting' },
              { element: <TargetingRunSheet />, path: ':programId/runs/:runId' },
            ],
            element: <CreditProgramsPage />,
            path: 'credit-programs',
          },
          {
            children: [
              { element: null, index: true },
              { element: <PaymentDetailSheet />, path: ':paymentAttemptId' },
            ],
            element: <PaymentsPage />,
            path: 'payments',
          },
          { element: <WalletsPage />, path: 'wallets' },
          { element: <WalletsPage />, path: 'wallets/transactions' },
          { element: <WalletsPage />, path: 'wallets/top-ups' },
          { element: <WalletsPage />, path: 'wallets/withdrawals' },
          { element: <WalletsPage />, path: 'wallets/transfers' },
          {
            children: [
              { element: null, index: true },
              { element: <ReturnRequestDetailSheet />, path: ':returnRequestId' },
            ],
            element: <ReturnRequestsPage />,
            path: 'return-requests',
          },
          {
            children: [
              { element: null, index: true },
              { element: <DelinquencyCasePage />, path: ':caseId' },
            ],
            element: <DelinquencyCasesPage />,
            path: 'delinquency-cases',
          },
          { element: <WriteOffsPage />, path: 'write-offs' },
          {
            children: [
              { element: null, index: true },
              { element: <SettlementBatchSheet />, path: ':batchId' },
            ],
            element: <SettlementsPage />,
            path: 'settlements',
          },
          {
            children: [
              { element: null, index: true },
              { element: <CashSettlementBatchSheet />, path: ':batchId' },
            ],
            element: <SettlementCashPage />,
            path: 'settlements/cash',
          },
          { element: <SettlementAttemptsPage />, path: 'settlements/attempts' },
          { element: <SettlementHoldsPage />, path: 'settlements/holds' },
          { element: <SettlementRecoveryPage />, path: 'settlements/recovery' },
          { element: <SettlementReconciliationPage />, path: 'settlements/reconciliation' },
          { element: <MyCommissionsPage />, path: 'my-commissions' },
          {
            children: [
              { element: <MarketingSummaryPage />, index: true },
              { element: <MarketingRecipientsPage />, path: 'recipients' },
              { element: <MarketingRecipientPage />, path: 'recipients/:recipientId' },
              { element: <MarketingStructuresPage />, path: 'structures' },
              { element: <MarketingStructurePage />, path: 'structures/:structureId' },
              { element: <MarketingAssignmentsPage />, path: 'assignments' },
              { element: <MarketingAssignmentPage />, path: 'assignments/:assignmentId' },
              { element: <MarketingCommissionsPage />, path: 'commissions' },
              { element: <CommissionPayoutsPage />, path: 'payouts' },
              { element: <CommissionPayoutPage />, path: 'payouts/:attemptId' },
              { element: <CommissionHoldsPage />, path: 'holds' },
            ],
            element: <MarketingLayout />,
            path: 'marketing',
          },
          { element: <CalendarPage />, path: 'calendar' },
          { element: <PoliciesPage />, path: 'policies' },
          { element: <PlatformAccessPage />, path: 'platform-access' },
          { element: <PhysicalCardsPage />, path: 'physical-cards' },
          { element: <PhysicalCardsPage />, path: 'physical-cards/:tab' },
          // The print export moved into «کارت‌های فیزیکی» (D-117); old links land on its tab.
          { element: <PhysicalCardExportRedirect />, path: 'physical-card-exports' },
          { element: <NotFoundPage />, path: '*' },
        ],
        element: <Layout />,
      },
    ],
    element: (
      <DirectionProvider direction="rtl">
        <AuthGate>
          <Outlet />
        </AuthGate>
      </DirectionProvider>
    ),
    path: '/',
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
